import React, { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { I, Icon } from '../shared/ns-icons'
import { nsConfirm } from '../shared/ns-confirm'
import { NSSelect } from '../shared/ns-kit'

// ── Small helpers ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button" role="switch" aria-checked={!!checked}
        onClick={() => onChange(!checked)}
        style={{ width: 34, height: 19, borderRadius: 999, flexShrink: 0, background: checked ? 'var(--accent)' : 'var(--surface-2)', position: 'relative', transition: 'background 0.15s ease', border: '1px solid var(--border)' }}
      >
        <span style={{ position: 'absolute', top: 1, left: checked ? 16 : 1, width: 15, height: 15, borderRadius: '50%', background: 'white', transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
      </button>
      {label && <span style={{ fontSize: 12.5, color: 'var(--text-soft)' }}>{label}</span>}
    </span>
  )
}

function SetSection({ title, desc, children }) {
  return (
    <div className="card card-pad">
      <div style={{ marginBottom: 18 }}>
        <h2 className="h2">{title}</h2>
        {desc && <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{desc}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{children}</div>
    </div>
  )
}

function SetField({ label, hint, children, inline = true }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: inline ? '240px 1fr' : '1fr', gap: 8, alignItems: inline ? 'center' : 'stretch' }}>
      <div>
        <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  )
}

const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" /><path d="M6.6 6.6A18.5 18.5 0 0 0 1 12s4 8 11 8a9.1 9.1 0 0 0 5.4-1.6" />
  </svg>
)

function SetLogoSlot({ label, hint, dark, small, field, value, onUpload, onRemove, uploading }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: small ? 80 : 100, borderRadius: 10, background: dark ? 'var(--bg-3, #1a1625)' : 'white', border: '1.5px dashed var(--border-strong)', marginBottom: 8, overflow: 'hidden' }}>
        {value
          ? <img src={value} alt={label} style={{ maxHeight: '78%', maxWidth: '80%', objectFit: 'contain' }} />
          : <Icon name="image" style={{ color: 'var(--text-faint)', fontSize: small ? 24 : 30 }} />}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <label className="btn btn-sm" style={{ flex: 1, justifyContent: 'center', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
          <I.upload /> {uploading ? 'Uploading…' : 'Replace'}
          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading} onChange={e => { if (e.target.files?.[0]) onUpload(e.target.files[0], field) }} />
        </label>
        <button className="btn btn-sm" onClick={() => onRemove(field)}>Remove</button>
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{hint}</div>}
    </div>
  )
}

// ── Nav config ────────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'brand',        label: 'Branding',      icon: 'image' },
  { id: 'security',     label: 'Security',      icon: 'shield' },
  { id: 'email',        label: 'Email',         icon: 'send' },
  { id: 'plans',        label: 'Plans',         icon: 'card' },
  { id: 'custom-plans', label: 'Custom plans',  icon: 'sliders' },
]

// Tabs whose form is saved by the header "Save changes" button.
const SAVE_SECTION = { brand: 'branding', security: 'security', email: 'email' }

// ── Brand tab ─────────────────────────────────────────────────────────────────
function BrandTab({ formData, setFormData }) {
  const [uploading, setUploading] = useState({})

  const handleUpload = async (file, field) => {
    setUploading(prev => ({ ...prev, [field]: true }))
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await apiService.post('/settings/branding/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setFormData(prev => ({ ...prev, branding: { ...prev.branding, [field]: res.data.url } }))
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }))
    }
  }

  const handleRemove = (field) => setFormData(prev => ({ ...prev, branding: { ...prev.branding, [field]: '' } }))
  const setB = (k, v) => setFormData(prev => ({ ...prev, branding: { ...prev.branding, [k]: v } }))

  return (
    <>
      <SetSection title="Workspace identity" desc="How this CMS appears to your clients">
        <SetField label="CMS name">
          <input className="input" value={formData.branding?.cmsName || ''} onChange={e => setB('cmsName', e.target.value)} placeholder="WebNest" />
        </SetField>
        <SetField label="Support email">
          <input className="input" type="email" value={formData.branding?.supportEmail || ''} onChange={e => setB('supportEmail', e.target.value)} placeholder="support@youragency.com" />
        </SetField>
        <SetField label="Login page tagline" hint="Shown on the client login page">
          <input className="input" value={formData.branding?.tagline || ''} onChange={e => setB('tagline', e.target.value)} placeholder="Powering your online presence" />
        </SetField>
        <SetField label="Copyright text">
          <input className="input" value={formData.branding?.copyrightText || ''} onChange={e => setB('copyrightText', e.target.value)} placeholder="© 2024 WebNest. All rights reserved." />
        </SetField>
        <SetField label="App URL" hint="Where your clients sign in">
          <input className="input" type="url" value={formData.branding?.appUrl || ''} onChange={e => setB('appUrl', e.target.value)} placeholder="https://cms.youragency.com" />
        </SetField>
      </SetSection>

      <SetSection title="Logo & favicon" desc="Used in the admin sidebar, client portal and browser tabs">
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <SetLogoSlot label="Logo · dark mode" hint="White or light SVG / PNG" dark field="logoUrl" value={formData.branding?.logoUrl || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.logoUrl} />
          <SetLogoSlot label="Logo · light mode" hint="Dark or black SVG / PNG" field="logoUrlLight" value={formData.branding?.logoUrlLight || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.logoUrlLight} />
          <SetLogoSlot label="Favicon" hint="32×32 or 64×64" small field="faviconUrl" value={formData.branding?.faviconUrl || ''} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading.faviconUrl} />
        </div>
      </SetSection>
    </>
  )
}

// ── Security tab ──────────────────────────────────────────────────────────────
function SecurityTab({ formData, setFormData, adminSecurityInfo, setShow2FAModal }) {
  const setS = (k, v) => setFormData(prev => ({ ...prev, security: { ...prev.security, [k]: v } }))

  return (
    <>
      <SetSection title="Your account 2FA" desc="Protect your admin account with an authenticator app">
        <SetField
          label="Two-factor authentication"
          hint={adminSecurityInfo?.totp_enabled ? 'Enabled — protected with an authenticator app' : 'Add an extra layer of security to your account'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {adminSecurityInfo?.totp_enabled && <span className="badge badge-success"><I.check /> On</span>}
            <button
              className="btn btn-sm"
              style={adminSecurityInfo?.totp_enabled ? { color: 'var(--danger)', borderColor: 'var(--danger)' } : {}}
              onClick={() => setShow2FAModal(true)}
            >
              {adminSecurityInfo?.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
            </button>
          </div>
        </SetField>
      </SetSection>

      <SetSection title="Authentication" desc="Sign-in security for the whole workspace">
        <SetField label="Require 2FA for all admins" hint="Force two-factor authentication workspace-wide">
          <Toggle checked={formData.security?.requireTwoFactor || false} onChange={v => setS('requireTwoFactor', v)} />
        </SetField>
        <SetField label="Session timeout" hint="Auto sign-out after inactivity">
          <NSSelect
            value={formData.security?.sessionTimeout || 24}
            onChange={e => setS('sessionTimeout', parseInt(e.target.value))}
            options={[
              { value: 1, label: '1 hour' },
              { value: 4, label: '4 hours' },
              { value: 8, label: '8 hours' },
              { value: 24, label: '24 hours' },
              { value: 720, label: '30 days' },
            ]}
          />
        </SetField>
        <SetField label="Max login attempts">
          <input className="input" type="number" min={1} max={20} value={formData.security?.maxLoginAttempts || 5} onChange={e => setS('maxLoginAttempts', parseInt(e.target.value))} />
        </SetField>
        <SetField label="Min password length">
          <input className="input" type="number" min={6} max={64} value={formData.security?.passwordMinLength || 8} onChange={e => setS('passwordMinLength', parseInt(e.target.value))} />
        </SetField>
        <SetField label="Force HTTPS" hint="Always redirect to a secure connection">
          <Toggle checked={formData.security?.forceHttps !== false} onChange={v => setS('forceHttps', v)} />
        </SetField>
      </SetSection>
    </>
  )
}

// ── Email tab (SMTP config) ───────────────────────────────────────────────────
function EmailTab({ formData, setFormData, emailTestStatus, emailTestMessage, handleTestEmailConnection }) {
  const [showPw, setShowPw] = useState(false)
  const setE = (k, v) => setFormData(prev => ({ ...prev, email: { ...prev.email, [k]: v } }))

  return (
    <SetSection title="Email & notifications" desc="SMTP server used to send client emails">
      <SetField label="SMTP host">
        <input className="input" value={formData.email?.smtpHost || ''} onChange={e => setE('smtpHost', e.target.value)} placeholder="smtp.gmail.com" />
      </SetField>
      <SetField label="SMTP port">
        <input className="input" type="number" value={formData.email?.smtpPort || 587} onChange={e => setE('smtpPort', parseInt(e.target.value))} placeholder="587" />
      </SetField>
      <SetField label="Username">
        <input className="input" value={formData.email?.smtpUsername || ''} onChange={e => setE('smtpUsername', e.target.value)} placeholder="your@email.com" />
      </SetField>
      <SetField label="Password">
        <div style={{ position: 'relative' }}>
          <input className="input" type={showPw ? 'text' : 'password'} value={formData.email?.smtpPassword || ''} onChange={e => setE('smtpPassword', e.target.value)} placeholder="••••••••" style={{ paddingRight: 38 }} />
          <button type="button" onClick={() => setShowPw(s => !s)} aria-label="Toggle password visibility" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
            {showPw ? <EyeOff /> : <I.eye />}
          </button>
        </div>
      </SetField>
      <SetField label="From email">
        <input className="input" type="email" value={formData.email?.fromEmail || ''} onChange={e => setE('fromEmail', e.target.value)} placeholder="noreply@example.com" />
      </SetField>
      <SetField label="From name">
        <input className="input" value={formData.email?.fromName || ''} onChange={e => setE('fromName', e.target.value)} placeholder="WebNest CMS" />
      </SetField>
      <SetField label="Enable TLS" hint="Use a secure TLS connection for delivery">
        <Toggle checked={formData.email?.enableTls || false} onChange={v => setE('enableTls', v)} />
      </SetField>

      <div className="divider" style={{ margin: '4px 0' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-sm" onClick={handleTestEmailConnection} disabled={emailTestStatus === 'testing'}>
          {emailTestStatus === 'testing' ? 'Testing…' : 'Test connection'}
        </button>
        {emailTestStatus === 'success' && (
          <span style={{ fontSize: 12.5, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 6 }}><I.check /> {emailTestMessage}</span>
        )}
        {emailTestStatus === 'error' && (
          <span style={{ fontSize: 12.5, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}><I.x /> {emailTestMessage}</span>
        )}
      </div>
    </SetSection>
  )
}

// ── Plans tab ─────────────────────────────────────────────────────────────────
function PlansTab({ formData, isSaving, setIsSaving, updatePlansMutation }) {
  const [editingPlan, setEditingPlan] = useState(null)
  const [planForm, setPlanForm] = useState(null)
  const PLAN_ORDER = ['basic', 'pro', 'enterprise']

  const handleEditPlan = (planId) => { setEditingPlan(planId); setPlanForm({ ...formData.plans[planId] }) }
  const handleSavePlan = () => {
    const updatedPlans = { ...formData.plans, [editingPlan]: planForm }
    setIsSaving(true)
    updatePlansMutation.mutate(updatedPlans)
    setEditingPlan(null); setPlanForm(null)
  }
  const handleCancel = () => { setEditingPlan(null); setPlanForm(null) }
  const setPF = (k, v) => setPlanForm(p => ({ ...p, [k]: v }))
  const setFeat = (k, v) => setPlanForm(p => ({ ...p, features: { ...p.features, [k]: v } }))

  return (
    <>
      <SetSection title="Subscription plans" desc="Manage client subscription plans, pricing, and features">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12 }}>
          {PLAN_ORDER.filter(k => formData.plans?.[k]).map(planId => {
            const plan = formData.plans[planId]
            const popular = planId === 'pro'
            return (
              <div key={planId} className={`card card-pad ${popular ? 'card-glow' : ''}`} style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {popular && <span className="badge badge-accent" style={{ position: 'absolute', top: 14, right: 14 }}>Most popular</span>}
                <h3 className="h3">{plan.name}</h3>
                <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 8 }}>
                  £{plan.price}<span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /month</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <I.check style={{ color: 'var(--success)', flexShrink: 0 }} /><span>{plan.features?.sites === 'unlimited' ? 'Unlimited' : plan.features?.sites} site{plan.features?.sites === 1 ? '' : 's'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <I.check style={{ color: 'var(--success)', flexShrink: 0 }} /><span>{plan.features?.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features?.editsPerMonth} edits/mo</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <I.check style={{ color: 'var(--success)', flexShrink: 0 }} /><span>{plan.features?.storage}</span>
                  </div>
                  {[{ key: 'customDomain', label: 'Custom domain' }, { key: 'analytics', label: 'Analytics' }, { key: 'backup', label: 'Backup' }].map(({ key, label }) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: plan.features?.[key] ? 'var(--text)' : 'var(--text-faint)' }}>
                      <I.check style={{ color: plan.features?.[key] ? 'var(--success)' : 'var(--border-strong)', flexShrink: 0 }} /><span>{label}</span>
                    </div>
                  ))}
                </div>
                <button className="btn btn-sm" style={{ marginTop: 18, width: '100%' }} onClick={() => handleEditPlan(planId)}><I.edit /> Edit plan</button>
              </div>
            )
          })}
        </div>
      </SetSection>

      {editingPlan && planForm && (
        <SetSection title={`Edit plan · ${planForm.name}`} desc="Changes apply to new and existing subscribers">
          <SetField label="Plan name"><input className="input" value={planForm.name} onChange={e => setPF('name', e.target.value)} /></SetField>
          <SetField label="Price (£/month)"><input className="input" type="number" value={planForm.price} onChange={e => setPF('price', parseFloat(e.target.value) || 0)} /></SetField>
          <SetField label="Number of sites" hint="Enter 'unlimited' for unlimited">
            <input className="input" value={planForm.features?.sites === 'unlimited' ? 'unlimited' : planForm.features?.sites} onChange={e => setFeat('sites', e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1)} />
          </SetField>
          <SetField label="Edits per month" hint="Enter 'unlimited' for unlimited">
            <input className="input" value={planForm.features?.editsPerMonth === 'unlimited' ? 'unlimited' : planForm.features?.editsPerMonth} onChange={e => setFeat('editsPerMonth', e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50)} />
          </SetField>
          <SetField label="Storage"><input className="input" value={planForm.features?.storage || ''} onChange={e => setFeat('storage', e.target.value)} placeholder="1GB Storage" /></SetField>
          <SetField label="Support type"><input className="input" value={planForm.features?.support || ''} onChange={e => setFeat('support', e.target.value)} placeholder="Email Support" /></SetField>
          <SetField label="Stripe Price ID" hint="optional"><input className="input" value={planForm.stripePriceId || ''} onChange={e => setPF('stripePriceId', e.target.value)} placeholder="price_…" /></SetField>
          <SetField label="Stripe Product ID" hint="optional"><input className="input" value={planForm.stripeProductId || ''} onChange={e => setPF('stripeProductId', e.target.value)} placeholder="prod_…" /></SetField>
          <SetField label="Features">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {['customDomain', 'analytics', 'backup'].map(key => (
                <Toggle key={key} checked={planForm.features?.[key] || false} onChange={v => setFeat(key, v)} label={{ customDomain: 'Custom domain', analytics: 'Analytics', backup: 'Backup' }[key]} />
              ))}
            </div>
          </SetField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={handleSavePlan} disabled={isSaving}><I.check /> Save plan</button>
          </div>
        </SetSection>
      )}
    </>
  )
}

// ── Custom plans tab ──────────────────────────────────────────────────────────
function CustomPlansTab({ customPlans, isSaving, setIsSaving, createCustomPlanMutation, updateCustomPlanMutation, deleteCustomPlanMutation }) {
  const blankForm = () => ({
    name: '', price: 0, description: '', targetType: 'custom', targetEmails: [],
    stripePriceId: '', stripeProductId: '',
    features: { sites: 1, editsPerMonth: 50, storage: '1GB Storage', support: 'Email Support', customDomain: false, analytics: false, backup: false, priority: 'normal' }
  })

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(blankForm())
  const [emailInput, setEmailInput] = useState('')

  const addEmail = () => {
    const e = emailInput.trim()
    if (e && !form.targetEmails.includes(e)) { setForm(p => ({ ...p, targetEmails: [...p.targetEmails, e] })); setEmailInput('') }
  }
  const removeEmail = (e) => setForm(p => ({ ...p, targetEmails: p.targetEmails.filter(x => x !== e) }))
  const setFeat = (k, v) => setForm(p => ({ ...p, features: { ...p.features, [k]: v } }))

  const handleCreate = () => { setIsSaving(true); createCustomPlanMutation.mutate(form); setShowForm(false); setForm(blankForm()) }
  const handleUpdate = () => { setIsSaving(true); updateCustomPlanMutation.mutate({ planId: editingId, planData: form }); setEditingId(null); setShowForm(false); setForm(blankForm()) }
  const handleEdit = (plan) => {
    setEditingId(plan.id)
    setForm({ name: plan.name, price: plan.price, description: plan.description || '', targetType: plan.targetType, targetEmails: plan.targetEmails || [], stripePriceId: plan.stripePriceId || '', stripeProductId: plan.stripeProductId || '', features: { ...plan.features } })
    setShowForm(true)
  }
  const handleDelete = async (planId) => {
    const ok = await nsConfirm({
      tone: 'danger',
      title: 'Delete custom plan?',
      message: 'This permanently removes the plan. Existing subscribers keep their current pricing.',
      detail: 'This cannot be undone',
      confirmLabel: 'Delete plan',
    })
    if (ok) deleteCustomPlanMutation.mutate(planId)
  }
  const handleCancel = () => { setShowForm(false); setEditingId(null); setForm(blankForm()) }

  return (
    <>
      <SetSection title="Custom plans" desc="Special pricing for friends, family, or specific customers">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-sm btn-primary" onClick={() => { setEditingId(null); setForm(blankForm()); setShowForm(true) }}><I.plus /> Create custom plan</button>
        </div>

        {customPlans && Object.keys(customPlans).length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 12 }}>
            {Object.values(customPlans).map(plan => (
              <div key={plan.id} className="card card-pad-sm" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{plan.name}</span>
                  <span className="badge">{plan.targetType}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>£{plan.price}<span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}>/mo</span></div>
                {plan.description && <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 8 }}>{plan.description}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {plan.features?.sites === 'unlimited' ? 'Unlimited' : plan.features?.sites} sites · {plan.features?.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features?.editsPerMonth} edits/mo · {plan.features?.storage}
                </div>
                {plan.targetEmails?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                    {plan.targetEmails.map(e => <span key={e} className="badge" style={{ fontSize: 10 }}>{e}</span>)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
                  <button className="btn btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => handleEdit(plan)}><I.edit /> Edit</button>
                  <button className="btn btn-sm" style={{ flex: 1, justifyContent: 'center', color: 'var(--danger)' }} onClick={() => handleDelete(plan.id)}><I.trash /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-faint)', fontSize: 13 }}>No custom plans yet. Create one to offer special pricing.</div>
        )}
      </SetSection>

      {showForm && (
        <SetSection title={editingId ? 'Edit custom plan' : 'Create custom plan'} desc="Define pricing and feature limits for this plan">
          <SetField label="Plan name"><input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Friends & Family Plan" /></SetField>
          <SetField label="Price (£/month)"><input className="input" type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} /></SetField>
          <SetField label="Description" hint="optional"><textarea className="input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Special pricing for friends and family" style={{ resize: 'vertical' }} /></SetField>
          <SetField label="Plan type">
            <div style={{ display: 'flex', gap: 16 }}>
              {[{ value: 'friend', label: 'Friends' }, { value: 'family', label: 'Family' }, { value: 'custom', label: 'Custom' }].map(({ value, label }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" name="customPlanType" checked={form.targetType === value} onChange={() => setForm(p => ({ ...p, targetType: value }))} /> {label}
                </label>
              ))}
            </div>
          </SetField>
          <SetField label="Target emails" hint="press Enter or click Add">
            <div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="input" style={{ flex: 1 }} type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())} placeholder="user@example.com" />
                <button type="button" className="btn btn-sm" onClick={addEmail}>Add</button>
              </div>
              {form.targetEmails.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {form.targetEmails.map(e => (
                    <span key={e} className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{e}<button onClick={() => removeEmail(e)} aria-label="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}><I.x /></button></span>
                  ))}
                </div>
              )}
            </div>
          </SetField>
          <SetField label="Sites"><input className="input" value={form.features.sites === 'unlimited' ? 'unlimited' : form.features.sites} onChange={e => setFeat('sites', e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 1)} /></SetField>
          <SetField label="Edits/month"><input className="input" value={form.features.editsPerMonth === 'unlimited' ? 'unlimited' : form.features.editsPerMonth} onChange={e => setFeat('editsPerMonth', e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value) || 50)} /></SetField>
          <SetField label="Storage"><input className="input" value={form.features.storage} onChange={e => setFeat('storage', e.target.value)} placeholder="1GB Storage" /></SetField>
          <SetField label="Support type"><input className="input" value={form.features.support} onChange={e => setFeat('support', e.target.value)} placeholder="Email Support" /></SetField>
          <SetField label="Stripe Price ID" hint="optional"><input className="input" value={form.stripePriceId} onChange={e => setForm(p => ({ ...p, stripePriceId: e.target.value }))} placeholder="price_…" /></SetField>
          <SetField label="Stripe Product ID" hint="optional"><input className="input" value={form.stripeProductId} onChange={e => setForm(p => ({ ...p, stripeProductId: e.target.value }))} placeholder="prod_…" /></SetField>
          <SetField label="Features">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {['customDomain', 'analytics', 'backup'].map(key => (
                <Toggle key={key} checked={form.features[key] || false} onChange={v => setFeat(key, v)} label={{ customDomain: 'Custom domain', analytics: 'Analytics', backup: 'Backup' }[key]} />
              ))}
            </div>
          </SetField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-sm btn-primary" onClick={editingId ? handleUpdate : handleCreate} disabled={!form.name || form.price < 0 || isSaving}>{editingId ? 'Update plan' : 'Create plan'}</button>
          </div>
        </SetSection>
      )}
    </>
  )
}

// ── 2FA modal ─────────────────────────────────────────────────────────────────
function AdminTwoFactorModal({ isEnabled, onClose }) {
  const [step, setStep] = useState(isEnabled ? 'disable' : 'info')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSetup = async () => {
    setIsLoading(true); setError('')
    try {
      const response = await apiService.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode); setSecret(response.data.secret); setStep('setup')
    } catch (err) { setError(err.response?.data?.error || 'Failed to setup 2FA') } finally { setIsLoading(false) }
  }
  const handleVerify = async (e) => {
    e?.preventDefault(); setIsLoading(true); setError('')
    try {
      await apiService.post('/auth/2fa/verify', { token: code })
      setStep('success'); toast.success('2FA enabled successfully!'); setTimeout(onClose, 2000)
    } catch (err) { setError(err.response?.data?.error || 'Invalid verification code') } finally { setIsLoading(false) }
  }
  const handleDisable = async (e) => {
    e?.preventDefault(); setIsLoading(true); setError('')
    try {
      await apiService.post('/auth/2fa/disable', { token: code })
      setStep('disabled'); toast.success('2FA disabled'); setTimeout(onClose, 2000)
    } catch (err) { setError(err.response?.data?.error || 'Invalid authenticator code') } finally { setIsLoading(false) }
  }

  const otpInput = (onSubmit) => (
    <form onSubmit={onSubmit}>
      <input className="input" type="text" inputMode="numeric" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoFocus required
        style={{ textAlign: 'center', letterSpacing: '0.5em', fontFamily: 'var(--font-mono)', fontSize: 20 }} />
    </form>
  )
  const errorBox = error && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--danger)', background: 'var(--danger-soft, rgba(239,68,68,.1))', borderRadius: 8, padding: '8px 12px' }}><I.x /> {error}</div>
  )

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: 440, maxWidth: '100%', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>Two-factor authentication</span>
          <button className="site-action" onClick={onClose} aria-label="Close"><I.x /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {step === 'info' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Adds an extra layer of security — you'll need a code from your authenticator app each time you log in.</p>
              <ul style={{ fontSize: 13, color: 'var(--text-muted)', paddingLeft: 18, lineHeight: 1.8, margin: 0 }}>
                <li>Google Authenticator</li><li>Authy</li><li>Any TOTP-compatible app</li>
              </ul>
              {errorBox}
            </>
          )}
          {step === 'setup' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.</p>
              {qrCode && <div style={{ display: 'flex', justifyContent: 'center', padding: 16, background: 'white', borderRadius: 10, border: '1px solid var(--border)' }}><img src={qrCode} alt="2FA QR Code" style={{ width: 160, height: 160 }} /></div>}
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 6 }}>Can't scan? Enter this key manually:</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, padding: '8px 12px', background: 'var(--bg-2)', borderRadius: 8, wordBreak: 'break-all' }}>{secret}</div>
              </div>
              {otpInput(handleVerify)}
              {errorBox}
            </>
          )}
          {step === 'disable' && (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Enter the current 6-digit code from your authenticator app to disable 2FA.</p>
              {otpInput(handleDisable)}
              {errorBox}
            </>
          )}
          {(step === 'success' || step === 'disabled') && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--success-soft, rgba(34,197,94,.15))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: 'var(--success)' }}><I.check /></div>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{step === 'success' ? '2FA enabled successfully!' : '2FA has been disabled'}</div>
            </div>
          )}
        </div>
        {!['success', 'disabled'].includes(step) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            {step === 'info' && (
              <>
                <button className="btn btn-sm" onClick={onClose}>Cancel</button>
                <button className="btn btn-sm btn-primary" onClick={handleSetup} disabled={isLoading}>{isLoading ? 'Loading…' : 'Get started'}</button>
              </>
            )}
            {step === 'setup' && (
              <>
                <button className="btn btn-sm" onClick={() => setStep('info')}>Back</button>
                <button className="btn btn-sm btn-primary" onClick={handleVerify} disabled={isLoading || code.length !== 6}>{isLoading ? 'Verifying…' : 'Verify & enable'}</button>
              </>
            )}
            {step === 'disable' && (
              <>
                <button className="btn btn-sm" onClick={onClose}>Cancel</button>
                <button className="btn btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={handleDisable} disabled={isLoading || code.length !== 6}>{isLoading ? 'Disabling…' : 'Disable 2FA'}</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('brand')
  const [isSaving, setIsSaving] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)
  const [emailTestStatus, setEmailTestStatus] = useState(null)
  const [emailTestMessage, setEmailTestMessage] = useState('')
  const queryClient = useQueryClient()

  const { data: adminSecurityInfo } = useQuery('admin-user-security', () => apiService.get('/auth/security').then(r => r.data), { staleTime: 0 })
  const { data: settings, isLoading } = useQuery('settings', () => apiService.get('/settings').then(res => res.data))
  const { data: billingPlans } = useQuery('billing-plans', () => apiService.get('/billing/plans').then(res => res.data))
  const { data: customPlans } = useQuery('custom-plans', () => apiService.get('/custom-plans').then(res => res.data))

  const updateSettingsMutation = useMutation(
    (newSettings) => apiService.put('/settings', newSettings).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('settings')
        queryClient.invalidateQueries('billing-plans')
        toast.success('Settings updated successfully')
        setIsSaving(false)
      },
      onError: (error) => { toast.error(error.response?.data?.message || 'Failed to update settings'); setIsSaving(false) }
    }
  )

  const updatePlansMutation = useMutation(
    (plans) => apiService.put('/billing/plans', plans).then(res => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('billing-plans')
        queryClient.invalidateQueries('settings')
        queryClient.refetchQueries('billing-plans')
        toast.success('Plans updated successfully')
        setIsSaving(false)
      },
      onError: (error) => { toast.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update plans'); setIsSaving(false) }
    }
  )

  const createCustomPlanMutation = useMutation(
    (planData) => apiService.post('/custom-plans', planData).then(res => res.data),
    {
      onSuccess: () => { queryClient.invalidateQueries('custom-plans'); queryClient.invalidateQueries('billing-plans'); toast.success('Custom plan created successfully'); setIsSaving(false) },
      onError: (error) => { toast.error(error.response?.data?.message || 'Failed to create custom plan'); setIsSaving(false) }
    }
  )

  const updateCustomPlanMutation = useMutation(
    ({ planId, planData }) => apiService.put(`/custom-plans/${planId}`, planData).then(res => res.data),
    {
      onSuccess: () => { queryClient.invalidateQueries('custom-plans'); queryClient.invalidateQueries('billing-plans'); toast.success('Custom plan updated successfully'); setIsSaving(false) },
      onError: (error) => { toast.error(error.response?.data?.message || 'Failed to update custom plan'); setIsSaving(false) }
    }
  )

  const deleteCustomPlanMutation = useMutation(
    (planId) => apiService.delete(`/custom-plans/${planId}`).then(res => res.data),
    {
      onSuccess: () => { queryClient.invalidateQueries('custom-plans'); queryClient.invalidateQueries('billing-plans'); toast.success('Custom plan deleted successfully') },
      onError: (error) => { toast.error(error.response?.data?.message || 'Failed to delete custom plan') }
    }
  )

  // ── Default settings structure ────────────────────────────────────────────
  const defaultSettings = {
    general: { siteName: 'WebNest CMS', siteDescription: 'Professional CMS for managing websites', adminEmail: 'admin@webnest.com', timezone: 'UTC', language: 'en', maintenanceMode: false, registrationEnabled: true },
    security: { requireTwoFactor: false, sessionTimeout: 24, maxLoginAttempts: 5, passwordMinLength: 8, requirePasswordChange: false, forceHttps: true, ipWhitelist: [], auditLogging: true },
    email: { provider: 'smtp', smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpUsername: '', smtpPassword: '', fromEmail: 'noreply@webnest.com', fromName: 'WebNest', enableTls: true, testConnection: 'pending' },
    storage: { maxFileSize: 10, allowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx'], storageQuota: 1000, backupEnabled: true, backupFrequency: 'daily', compressionEnabled: true, cdnEnabled: false },
    appearance: { primaryColor: '#533AFD', secondaryColor: '#928DAB', darkMode: true, companyLogo: '', favicon: '', customCss: '' },
    branding: { cmsName: 'WebNest', tagline: 'Powering your online presence', supportEmail: '', copyrightText: '© 2024 WebNest. All rights reserved.', logoUrl: '', logoUrlLight: '', faviconUrl: '', appUrl: '' },
    notifications: { emailNotifications: true, systemAlerts: true, maintenanceAlerts: true, securityAlerts: true, reportFrequency: 'weekly' },
    plans: {
      basic: { name: 'Basic', price: 29, features: { sites: 1, editsPerMonth: 50, storage: '1GB Storage', support: 'Email Support', customDomain: true, analytics: false, backup: false, priority: 'normal' } },
      pro: { name: 'Pro', price: 79, features: { sites: 5, editsPerMonth: 200, storage: '10GB Storage', support: 'Priority Support', customDomain: true, analytics: true, backup: true, priority: 'high' } },
      enterprise: { name: 'Enterprise', price: 199, features: { sites: 'unlimited', editsPerMonth: 'unlimited', storage: '100GB Storage', support: '24/7 Phone Support', customDomain: true, analytics: true, backup: true, priority: 'highest' } }
    }
  }

  const displaySettings = useMemo(() => {
    const baseSettings = settings || defaultSettings
    return {
      ...baseSettings,
      plans: billingPlans ? {
        basic:      billingPlans.basic      || baseSettings.plans.basic,
        pro:        billingPlans.pro        || baseSettings.plans.pro,
        enterprise: billingPlans.enterprise || baseSettings.plans.enterprise
      } : baseSettings.plans
    }
  }, [settings, billingPlans])

  const [formData, setFormData] = useState(displaySettings)

  useEffect(() => {
    if (!isSaving) setFormData(displaySettings)
  }, [displaySettings, isSaving])

  const handleSave = async (section) => {
    setIsSaving(true)
    if (section === 'plans') {
      updatePlansMutation.mutate(formData.plans)
    } else {
      updateSettingsMutation.mutate({ ...displaySettings, [section]: formData[section] })
    }
  }

  const handleTestEmailConnection = async () => {
    setEmailTestStatus('testing')
    setEmailTestMessage('')
    try {
      const res = await apiService.get('/emails/verify')
      if (res.data?.verified) {
        setEmailTestStatus('success')
        setEmailTestMessage('Connection successful — your SMTP settings are working.')
      } else {
        setEmailTestStatus('error')
        setEmailTestMessage(res.data?.message || 'Connection failed. Check your SMTP settings.')
      }
    } catch (err) {
      setEmailTestStatus('error')
      setEmailTestMessage(err.response?.data?.details || err.response?.data?.error || 'Could not reach the email server. Check your settings.')
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <LoadingSpinner size="lg" text="Loading settings…" />
      </div>
    )
  }

  const saveSection = SAVE_SECTION[activeTab]

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Settings</div>
          <h1>Settings</h1>
          <div className="subtitle">Branding, security, integrations and client plans</div>
        </div>
        <div className="flex gap-2 items-center">
          {saveSection && (
            <button className="btn btn-primary" onClick={() => handleSave(saveSection)} disabled={isSaving}>
              <I.check /> {isSaving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </div>
      </div>

      <div className="page-body">
        <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, alignItems: 'flex-start' }}>
          {/* Sidebar nav */}
          <div className="card card-pad-sm">
            {NAV_ITEMS.map(t => (
              <button key={t.id} className={`layer-row ${activeTab === t.id ? 'selected' : ''}`} style={{ width: '100%' }} onClick={() => setActiveTab(t.id)}>
                <Icon name={t.icon} style={{ color: t.danger ? 'var(--danger)' : 'var(--text-muted)', fontSize: 14 }} />
                <span style={{ flex: 1, textAlign: 'left', color: t.danger ? 'var(--danger)' : undefined }}>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Content pane */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {activeTab === 'brand' && <BrandTab formData={formData} setFormData={setFormData} />}
            {activeTab === 'security' && <SecurityTab formData={formData} setFormData={setFormData} adminSecurityInfo={adminSecurityInfo} setShow2FAModal={setShow2FAModal} />}
            {activeTab === 'email' && <EmailTab formData={formData} setFormData={setFormData} emailTestStatus={emailTestStatus} emailTestMessage={emailTestMessage} handleTestEmailConnection={handleTestEmailConnection} />}
            {activeTab === 'plans' && <PlansTab formData={formData} isSaving={isSaving} setIsSaving={setIsSaving} updatePlansMutation={updatePlansMutation} />}
            {activeTab === 'custom-plans' && <CustomPlansTab customPlans={customPlans} isSaving={isSaving} setIsSaving={setIsSaving} createCustomPlanMutation={createCustomPlanMutation} updateCustomPlanMutation={updateCustomPlanMutation} deleteCustomPlanMutation={deleteCustomPlanMutation} />}
          </div>
        </div>
      </div>

      {show2FAModal && (
        <AdminTwoFactorModal
          isEnabled={!!adminSecurityInfo?.totp_enabled}
          onClose={() => { setShow2FAModal(false); queryClient.invalidateQueries('admin-user-security') }}
        />
      )}
    </>
  )
}

export default AdminSettings
